import React, { useState, useEffect } from "react";
import { Modal, Button, Form, InputGroup, Row, Col, ListGroup, Card, CardBody } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaCcDinersClub, FaRegCreditCard } from "react-icons/fa";
import { CiCreditCard1 } from "react-icons/ci";
import { IoMdClose } from "react-icons/io"; // Import close icon
import IocnCamera from '../../images/check-icon.png';
import IocnSuccess from '../../images/success-icon.png';
import axios from "axios";
import apiConfig from "./apiConfig";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../../AuthProvider';
import "./style.scss";
/*
// DEMO

💳 4111 1111 1111 1111 -- Visa
💳 5555 5555 5555 4444 -- Mastercard
💳 3782 822463 10005 -- American Express
💳 6011 1111 1111 1117 -- Discover

*/const SubscriptionModal = ({
  show,
  handleClose,
  onSubscriptionSuccess,
  subscriptionSource = 'signup',
  currentPath = ''
}) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { login, logout } = useAuth(); // Get the logout function from auth context

  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [promoMessage, setPromoMessage] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedBilling, setSelectedBilling] = useState("month");
  const [totalAmount, setTotalAmount] = useState(0);
  const [subscriptionPriceId, setSubscriptionPriceId] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [couponId, setCouponId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [processingPayment, setProcessingPayment] = useState(false);
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);

  const selectedPlan = subscriptionPlans.find(
    (p) => p.billingInterval === selectedBilling
  );

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (show) {
      fetchSubscriptionPlan();
    }
  }, [show]);

  const handleBillingChange = (interval) => {
    if (interval === selectedBilling) return;
    const plan = subscriptionPlans.find((p) => p.billingInterval === interval);
    if (!plan) return;
    setSelectedBilling(interval);
    setTotalAmount(plan.price);
    setGrandTotal(plan.price);
    setSubscriptionPriceId(plan.stripePriceId);
    setPromoCode("");
    setPromoMessage(null);
    setDiscount(0);
    setDiscountAmount(0);
    setCouponId(null);
  };

  const handlePromoCodeChange = (e) => {
    const value = e.target.value;
    setPromoCode(value);

    // Clear promo error message when field is emptied
    if (!value) {
      setPromoMessage(null);
    }
  };

  const fetchSubscriptionPlan = async () => {
    try {
      setLoadingPlan(true);
      const response = await axios.get(
        `${apiConfig.apiBaseURL2}/getAllSubscriptionplans`,
        {
          headers: {
            "x-api-key": apiConfig["x-api-key2"]
          }
        }
      );

      if (response.data && response.data.success) {
        const plans = response.data.subscriptionPlans || [];
        setSubscriptionPlans(plans);
        const defaultPlan =
          plans.find((p) => p.billingInterval === "month") || plans[0];
        if (defaultPlan) {
          setSelectedBilling(defaultPlan.billingInterval);
          setTotalAmount(defaultPlan.price);
          setGrandTotal(defaultPlan.price);
          setSubscriptionPriceId(defaultPlan.stripePriceId);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
    } finally {
    setLoadingPlan(false);
  }
  };

  const handleApplyPromo = async () => {
    if (!promoCode) {
      setPromoMessage("Invalid promo code");
      toast.error("Please enter a valid promo code");
      return;
    }

    try {
      setApplyingPromo(true);
      const response = await axios.post(`${apiConfig.apiBaseURL}/validatePromoCode`, {
        code: promoCode,
        userId
      }, {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          "Content-Type": "application/json",
        }
      });

      if (response.data?.success) {
        const { discountAmount: discount, message, couponId } = response.data;
        const discountAmount = totalAmount * discount / 100;
        setDiscount(discount);
        setPromoMessage(message);
        setDiscountAmount(discountAmount);
        setGrandTotal(totalAmount - discountAmount);
        setCouponId(couponId);
        toast.success("Promo code applied successfully!");
      } else {
        setPromoMessage("Invalid promo code");
        toast.error(response.data?.message || "Invalid promo code");
      }
    } catch (error) {
      console.error("Error applying promo code:", error);
      setPromoMessage(error.response?.data?.message || "Invalid promo code");
      toast.error(error.response?.data?.message || "Error applying promo code");
    } finally {
    setApplyingPromo(false);
  }
  };

  const validationSchema = Yup.object({
    cardHolder: Yup.string().required("Cardholder name is required"),
  });

  const validateForm = () => {
    const errors = {};

    // Validate card elements from Stripe
    if (!elements) {
      return false;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    const cardExpiry = elements.getElement(CardExpiryElement);
    const cardCvc = elements.getElement(CardCvcElement);

    if (!cardNumber || cardNumber._empty) {
      errors.cardNumber = "Card number is required";
    }

    if (!cardExpiry || cardExpiry._empty) {
      errors.cardExpiry = "Expiry date is required";
    }

    if (!cardCvc || cardCvc._empty) {
      errors.cardCvc = "CVC is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Modified useEffect for Stripe elements
  useEffect(() => {
    if (elements) {
      const cardNumber = elements.getElement(CardNumberElement);
      const cardExpiry = elements.getElement(CardExpiryElement);
      const cardCvc = elements.getElement(CardCvcElement);

      if (cardNumber) {
        cardNumber.on('change', (event) => {
          // Clear error on any change - not just when complete
          setFormErrors(prev => ({ ...prev, cardNumber: null }));
        });
      }

      if (cardExpiry) {
        cardExpiry.on('change', (event) => {
          // Clear error on any change - not just when complete
          setFormErrors(prev => ({ ...prev, cardExpiry: null }));
        });
      }

      if (cardCvc) {
        cardCvc.on('change', (event) => {
          // Clear error on any change - not just when complete
          setFormErrors(prev => ({ ...prev, cardCvc: null }));
        });
      }

      // Cleanup function to remove event listeners when component unmounts
      return () => {
        if (cardNumber) cardNumber.off('change');
        if (cardExpiry) cardExpiry.off('change');
        if (cardCvc) cardCvc.off('change');
      };
    }
  }, [elements]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setProcessingPayment(true);

      // Validate form fields before proceeding
      const isValid = validateForm();

      // Check if there are validation errors
      if (!isValid) {
        setSubmitting(false);
        setProcessingPayment(false);
        return;
      }

      if (!stripe || !elements) {
        setProcessingPayment(false);
        return;
      }
      const cardElement = elements.getElement(CardNumberElement);
      const createPaymentMethod = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: values.cardHolder,
        },
      });

      const { error, paymentMethod } = createPaymentMethod;
      if (error) {
        console.error('Error creating payment method:', error);
        toast.error(error.message || "Error creating payment method");
        setSubmitting(false);
        setProcessingPayment(false);
      } else {
        const paymentMethodId = paymentMethod.id;
        const userId = localStorage.getItem("userId");

        const response = await axios.post(
          `${apiConfig.apiBaseURL}/createSubscription`,
          {
            paymentMethodId,
            userId,
            priceId: subscriptionPriceId,
            couponId,
          },
          {
            headers: {
              "x-api-key": apiConfig["x-api-key"],
              "Content-Type": "application/json",
            }
          }
        );

        if (response.data?.success) {
          const clientSecret = response.data.clientSecret;
          const confirmResult = await stripe.confirmCardPayment(clientSecret, {

            payment_method: paymentMethodId

          });
          if (confirmResult.error) {
            toast.error(confirmResult.error.message || "Payment failed");
            setProcessingPayment(false);
            // setLoading(false);
            return;
          }

          if (confirmResult.paymentIntent.status === "succeeded") {
            toast.success("Payment successful! Your subscription has been activated.");
            const authToken = response.data.token;
            const userId = response.data.userId || localStorage.getItem("userId");

            // Use the login function from AuthProvider to set the authentication state
            login(authToken, userId);

            // Update localStorage
            localStorage.setItem("authToken", authToken);
            localStorage.setItem('isLoggedIn', 'true');
            sessionStorage.setItem("authToken", authToken);

            // Trigger auth-updated event for any components listening for auth changes
            window.dispatchEvent(new Event('auth-updated'));

            // toast.success("Payment successful! Your subscription has been activated.");

            // Call onSubscriptionSuccess if provided
            if (onSubscriptionSuccess && typeof onSubscriptionSuccess === 'function') {
              onSubscriptionSuccess();
            }

            // Show different success messages based on subscription source
            let successTitle = "Welcome to AuditPro.ai!";
            let successText = "Your subscription has been activated successfully";
            let confirmButtonText = "Go to Dashboard";

             if (subscriptionSource === 'login') {
              successTitle = "Subscription Activated!";
              successText = "Thank you for subscribing to AuditPro";
              confirmButtonText = "Continue";
            }

            Swal.fire({
              allowOutsideClick: false,
              imageUrl: IocnSuccess,
              title: successTitle,
              text: successText,
              confirmButtonText: confirmButtonText,
              customClass: {
                container: "custom-swal-container",
                popup: "custom-swal-popup",
              },
            }).then((result) => {
              if (result.isConfirmed) {
                resetForm();
                handleClose(); // Close the modal first

                // Navigate based on subscription source
                if (confirmButtonText === "Go to Dashboard") {
                  navigate("/engagements");
                } else if (subscriptionSource === "login") {
                 localStorage.setItem('bypassBeforeUnload', 'true');
                 window.location.href = '/engagements';

                }
              }
            });
            // Handle login or redirect, as needed
          }
          // Get the auth token and user ID from the response

        }
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast.error(error.response?.data?.message || "Error creating subscription");
    } finally {
      setSubmitting(false);
      setProcessingPayment(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        backdrop="static"
        className="credential-page"
        keyboard={false}
        dialogClassName="modal-90w subscription-modal"
        centered
      >

        <Row>
          <Col lg={5} className="left p-4">
            <div className="left-aside p-4" style={{ borderRadius: '16px' }}>
              <div className="billing-toggle d-inline-flex mb-3 p-1" style={{ background: "rgba(255,255,255,0.15)", borderRadius: "999px" }}>
                <Button
                  variant={selectedBilling === "month" ? "light" : "link"}
                  size="sm"
                  className={`rounded-pill px-3 fw-bold ${selectedBilling === "month" ? "text-dark" : "text-white text-decoration-none"}`}
                  onClick={() => handleBillingChange("month")}
                  disabled={!subscriptionPlans.some((p) => p.billingInterval === "month")}
                >
                  Month
                </Button>
                <Button
                  variant={selectedBilling === "year" ? "light" : "link"}
                  size="sm"
                  className={`rounded-pill px-3 fw-bold ${selectedBilling === "year" ? "text-dark" : "text-white text-decoration-none"}`}
                  onClick={() => handleBillingChange("year")}
                  disabled={!subscriptionPlans.some((p) => p.billingInterval === "year")}
                >
                  Annual
                </Button>
              </div>

              <h2
                className="text-white mb-3 fw-bold"
                style={{ fontSize: "40px" }}
              >
                ${selectedPlan?.price ?? 0}{" "}
                <span style={{ fontSize: "18px", fontWeight: "normal" }}>
                  /{selectedBilling}
                </span>
              </h2>

              <div className="mb-4">
                <h3
                  className="text-white fw-bold mb-1"
                  style={{ fontSize: "20px" }}
                >
                  {selectedBilling === "year" ? "Annual Plan" : "Monthly Plan"}
                </h3>
                <p className="text-white" style={{ fontSize: "18px" }}>
                </p>
              </div>

              <h5 className="text-white fw-bold" style={{ fontSize: "16px" }}>
                What's included
              </h5>

             <Card className="overview-details">
  <CardBody>
    {loadingPlan ? (
      <div className="text-center py-2">
        <span className="spinner-border text-primary" role="status" />
        <span className="ms-2">Loading features...</span>
      </div>
    ) : (
      selectedPlan?.features?.map((item, key) => (
        <p key={key} className="text-dark" style={{ fontSize: "18px" }}>
          <img src={IocnCamera} alt="icon" className="me-2" />
          {item}
        </p>
      ))
    )}
  </CardBody>
</Card>

            </div>
          </Col>
          <Col lg={7} className="py-2">
            <div className="d-block px-3 pt-3">
              <h1
                className="text-dark text-center fw-bold mb-0"
                style={{ fontSize: "24px" }}
              >
                {subscriptionSource === 'expired' ? 'Renew Your Subscription' : 'Complete Your Subscription'}
              </h1>
              <p className="text-light text-center mb-0">
                {subscriptionSource === 'expired'
                  ? 'Your subscription has expired. Please renew to continue using AuditPro.ai'
                  : 'Get started with AuditPro.ai today'}
              </p>
            </div>

            <Modal.Body className="subscription-modal-body py-0">
              <Formik
                initialValues={{
                  cardNumber: "",
                  cardHolder: "",
                  expiryDate: "",
                  cvc: "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, submitForm, setFieldValue }) => (
                  <FormikForm>
                    <Row>
                      <Col md={12}>
                        <Form.Label>Group code</Form.Label>
                        <InputGroup className="mb-2">
                          <Form.Control
                            type="text"
                            placeholder="Enter Group code"
                            value={promoCode}
                            onChange={handlePromoCodeChange}
                            className="p-3 me-3"
                          />
                          <Button
                            variant="primary"
                            className="px-4 rounded fw-bold"
                            onClick={handleApplyPromo}
                            disabled={applyingPromo}
                          >
                          {applyingPromo ? (
    <>
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Applying...
    </>
  ) : (
    "Apply"
  )}
                          </Button>
                          {promoMessage ? (
                            <p
                              className={
                                discount
                                  ? "text-success w-100"
                                  : "text-danger w-100"
                              }
                            >
                              {promoMessage}
                            </p>
                          ) : null}
                        </InputGroup>

                        <hr />
                      </Col>


                      <Col md={12} className="mb-2">
                        <Form.Group className="position-relative">
                          <Form.Label>Card Number</Form.Label>
                          <CardNumberElement
                            options={{
                              showIcon: true,
                              iconStyle: 'solid',
                              style: {
                                base: {
                                  fontSize: '16px',
                                  color: '#000',
                                  '::placeholder': {
                                    color: '#a0aec0',
                                  },
                                },
                                invalid: {
                                  color: '#e53e3e',
                                },
                              },
                              disableLink: true
                            }}
                            className="form-control p-3"
                          />
                          {formErrors.cardNumber && (
                            <div className="text-danger">{formErrors.cardNumber}</div>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md={12} className="mb-2">

                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Card Holder Name</Form.Label>
                              <Field
                                name="cardHolder"
                                type="text"
                                className="form-control p-3"
                                onChange={(e) => {
                                  setFieldValue('cardHolder', e.target.value);
                                  setFormErrors(prev => ({ ...prev, cardHolder: null }));
                                }}
                              />
                              <ErrorMessage
                                name="cardHolder"
                                component="div"
                                className="text-danger"
                              />
                              {formErrors.cardHolder && (
                                <div className="text-danger">{formErrors.cardHolder}</div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label>Expiry Date</Form.Label>
                              <CardExpiryElement className="form-control p-3" />
                              {formErrors.cardExpiry && (
                                <div className="text-danger">{formErrors.cardExpiry}</div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label>CVC</Form.Label>
                              <CardCvcElement className="form-control p-3" />
                              {formErrors.cardCvc && (
                                <div className="text-danger">{formErrors.cardCvc}</div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>


                      </Col>

                    </Row>

                    <hr />

                    <h4 className="text-dark fw-bold" style={{ fontSize: "20px" }}>
                      Total
                    </h4>

                    <ListGroup as="ul">
                      <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between border-0 ps-0 py-0"
                      >
                        <p className="text-dark mb-0" style={{ fontSize: '18px' }}>{selectedBilling === "year" ? "Annual Plan" : "Monthly Plan"} <span className="text-light">(Billed {selectedBilling === "year" ? "Annually" : "Monthly"})</span></p>
                        <h4
                          className="fw-bold text-dark"
                          style={{ fontSize: "20px" }}
                        >
                          ${totalAmount.toFixed(2)}
                        </h4>
                      </ListGroup.Item>
                      <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between border-0 ps-0 py-0"
                      >
                        <p className="text-dark mb-0" style={{ fontSize: '18px' }}>Group Code</p>
                        <h4
                          className="fw-bold text-dark mb-0"
                          style={{ fontSize: "20px" }}
                        >
                          - ${(totalAmount * discount) / 100}
                        </h4>
                      </ListGroup.Item>
                    </ListGroup>

                    <hr className="py-0" />
                    <ListGroup.Item
                      as="li"
                      className="d-flex justify-content-between align-items-start border-0"
                    >
                      <div className="my-auto me-auto">
                        <div className="text-dark fw-bold" style={{ fontSize: "20px" }}>
                          Grand Total
                        </div>
                      </div>
                      <h4
                        className="fw-bold text-dark"
                        style={{ fontSize: "20px" }}
                      >
                        ${grandTotal?.toFixed(2)}
                      </h4>
                    </ListGroup.Item>

                    <Button
                      variant="primary"
                      type="button"
                      className="w-100 mt-1 py-3"
                      disabled={isSubmitting || processingPayment}
                      onClick={() => {
                        validateForm();
                        submitForm();
                      }}
                    >
                      {isSubmitting || processingPayment ? "Processing..." : (
                        subscriptionSource === 'expired' ? "Renew Subscription" : "Complete Purchase"
                      )}
                    </Button>
                  </FormikForm>
                )}
              </Formik>
              <p className="text-light mb-0" style={{ fontSize: "14px" }}>
                <CiCreditCard1 size={15} /> Secure payment
              </p>
            </Modal.Body>
          </Col>
        </Row>
      </Modal>
      {/* <ToastContainer /> */}
    </>
  );
};

export default SubscriptionModal;